import { ReactNode } from 'react';
import { withTime } from '../../lib/dates';

/**
 * Renders any payload object as readable label/value rows. Keys become
 * sentence-case labels, ISO dates become local dates, lists of primitives
 * become comma lists, lists of objects become small tables. Internal id
 * fields are dropped. Nothing is ever shown as raw JSON.
 */
interface Props { data: Record<string, unknown>; className?: string }

const HIDDEN = /(^|_)id$|_ids$/;

export function humanizeKey(key: string): string {
  const s = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function formatValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1);
  if (typeof v === 'string') {
    if (ISO.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return v.length > 10 ? withTime(d) : d.toLocaleDateString();
    }
    return v.replace(/_/g, ' ');
  }
  if (Array.isArray(v)) return v.map(formatValue).join(', ');
  return String(v);
}

function isObjectArray(v: unknown): v is Record<string, unknown>[] {
  return Array.isArray(v) && v.length > 0 && v.every((x) => x && typeof x === 'object' && !Array.isArray(x));
}

function Value({ v }: { v: unknown }): JSX.Element {
  if (isObjectArray(v)) {
    const cols = Array.from(new Set(v.flatMap((row) => Object.keys(row)))).filter((k) => !HIDDEN.test(k));
    return (
      <table className="mt-1 min-w-full text-xs">
        <thead><tr className="text-left text-bodydark2">{cols.map((c) => <th key={c} className="pr-3 py-1 font-medium">{humanizeKey(c)}</th>)}</tr></thead>
        <tbody>
          {v.map((row, i) => (
            <tr key={i} className="border-t border-stroke dark:border-strokedark">
              {cols.map((c) => <td key={c} className="pr-3 py-1">{formatValue(row[c])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return <EvidenceList data={v as Record<string, unknown>} className="mt-1 pl-3 border-l border-stroke dark:border-strokedark" />;
  }
  return <>{formatValue(v)}</>;
}

export default function EvidenceList({ data, className = '' }: Props) {
  const entries = Object.entries(data ?? {}).filter(([k]) => !HIDDEN.test(k));
  if (entries.length === 0) return <p className={`text-sm text-bodydark2 ${className}`}>No further detail.</p>;
  return (
    <dl className={`grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm ${className}`}>
      {entries.map(([k, v]) => (
        <ValueRow key={k} label={humanizeKey(k)}><Value v={v} /></ValueRow>
      ))}
    </dl>
  );
}

function ValueRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-bodydark2">{label}</dt>
      <dd className="min-w-0 text-black dark:text-white">{children}</dd>
    </>
  );
}
